import React, { useState } from "react";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSGrid from "../../system-design/DSGrid";
import DSAvatar from "../../system-design/DSAvatar";
import DSTooltip from "../../system-design/DSTooltip";
import DSChip from "../../system-design/DSChip";
import DSDialog from "../../system-design/DSDialog";
import { LocationOn, LocalOffer } from "@mui/icons-material";
import KlydoLoopUrl from "../../../components/klydoPreview/KlydoLoopUrl";
import { Klydo } from "../../../api/klydo";
import { PersonalFeedHistory } from "../../../api/personalFeed";
import { WithRequiredId } from "./CalendarEventDialog";
import {
  CalendarEvent,
  PopulatedCalendarEvent,
} from "../../../api/calendarEvent";
import KlydoModal from "../../Klydos/components/KlydoModal";

interface CalendarEventPreviewProps {
  calendarEvent:
    | WithRequiredId<PersonalFeedHistory>
    | WithRequiredId<PopulatedCalendarEvent>
    | WithRequiredId<CalendarEvent>;
}

const CalendarEventPreview: React.FC<CalendarEventPreviewProps> = ({
  calendarEvent,
}) => {
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | null>(null);
  const [klydoModalOpen, setKlydoModalOpen] = useState(false);

  const isPersonalFeedHistory = (
    data:
      | WithRequiredId<PersonalFeedHistory>
      | WithRequiredId<PopulatedCalendarEvent>
      | WithRequiredId<CalendarEvent>,
  ): data is PersonalFeedHistory => {
    return "displayedKlydos" in data;
  };

  const klydosData = isPersonalFeedHistory(calendarEvent)
    ? calendarEvent.displayedKlydos
    : (calendarEvent as PopulatedCalendarEvent).klydos?.map((klydo: Klydo) => ({
        klydo,
        clockIds: [],
      })) || [];

  const klydosCount = klydosData.length;
  const sectionTitle = isPersonalFeedHistory(calendarEvent)
    ? `Displayed Klydos (${klydosCount})`
    : `Available Klydos (${klydosCount})`;

  const handleKlydoClick = (klydo: Klydo) => {
    setSelectedKlydo(klydo);
    setKlydoModalOpen(true);
  };

  return (
    <DSDialogContent>
      <DSBox sx={{ p: 2 }}>
        <DSTypography variant="h5" gutterBottom>
          {calendarEvent.name}
        </DSTypography>
        <DSBox sx={{ mb: 3 }}>
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            <LocationOn color="action" />
            <DSTypography variant="body1">
              {calendarEvent?.country?.name}
              {calendarEvent?.states &&
                `, ${calendarEvent.states.map((s) => s.name).join(", ")}`}
            </DSTypography>
          </DSBox>

          {calendarEvent?.primaryType && (
            <DSBox sx={{ mb: 2 }}>
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <LocalOffer color="action" />
                <DSTypography variant="body2" fontWeight="bold">
                  Primary Type:
                </DSTypography>
              </DSBox>
              <DSChip
                label={calendarEvent.primaryType}
                color="primary"
                variant="outlined"
              />
            </DSBox>
          )}

          {calendarEvent.type && calendarEvent.type.length > 0 && (
            <DSBox sx={{ mb: 2 }}>
              <DSTypography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                Event Types:
              </DSTypography>
              <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {calendarEvent.type.map((type, index) => (
                  <DSChip
                    key={index}
                    label={type}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </DSBox>
            </DSBox>
          )}

          {calendarEvent.description && (
            <DSBox sx={{ mb: 2 }}>
              <DSTypography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                Description:
              </DSTypography>
              <DSTypography variant="body2" color="text.secondary">
                {calendarEvent?.description}
              </DSTypography>
            </DSBox>
          )}
        </DSBox>

        <DSTypography variant="h6" sx={{ mb: 2 }}>
          {sectionTitle}
        </DSTypography>

        {klydosData.length > 0 ? (
          <DSGrid container spacing={2}>
            {klydosData.map(
              (
                displayedKlydo: { klydo: Klydo; clockIds: string[] },
                klydoIndex: number,
              ) => (
                <DSGrid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`${displayedKlydo.klydo.id}-${klydoIndex}`}
                >
                  <DSBox
                    onClick={() => handleKlydoClick(displayedKlydo.klydo)}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      backgroundColor: "white.5",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        boxShadow: 2,
                        transform: "translateY(-2px)",
                        borderColor: "rgba(255, 216, 72, 0.6)",
                      },
                    }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      <DSBox
                        sx={{
                          width: 60,
                          height: 60,
                          overflow: "hidden",
                          borderRadius: "50%",
                        }}
                      >
                        {displayedKlydo.klydo.loopUrl ? (
                          <KlydoLoopUrl
                            loopUrl={displayedKlydo.klydo.loopUrl}
                          />
                        ) : (
                          <DSAvatar
                            src={
                              displayedKlydo.klydo.klydoSecureUrl || undefined
                            }
                            sx={{ width: "100%", height: "100%" }}
                          >
                            {displayedKlydo.klydo.name.charAt(0)}
                          </DSAvatar>
                        )}
                      </DSBox>

                      <DSTooltip title={displayedKlydo.klydo.name}>
                        <DSTypography
                          variant="body2"
                          fontWeight="bold"
                          textAlign="center"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {displayedKlydo.klydo.name}
                        </DSTypography>
                      </DSTooltip>

                      <DSTypography
                        variant="caption"
                        color="text.secondary"
                        textAlign="center"
                      >
                        by{" "}
                        {displayedKlydo.klydo.author.name ||
                          displayedKlydo.klydo.creator}
                      </DSTypography>

                      {isPersonalFeedHistory(calendarEvent) && (
                        <DSBox sx={{ mt: 1, width: "100%" }}>
                          <DSTypography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Number of clocks that received this Klydo: (
                            {displayedKlydo.clockIds.length})
                          </DSTypography>
                        </DSBox>
                      )}
                    </DSBox>
                  </DSBox>
                </DSGrid>
              ),
            )}
          </DSGrid>
        ) : (
          <DSBox
            sx={{
              textAlign: "center",
              py: 4,
              backgroundColor: "#f9f9f9",
              borderRadius: 2,
            }}
          >
            <DSTypography variant="body2" color="text.secondary">
              {isPersonalFeedHistory(calendarEvent)
                ? "No klydos were displayed for this calendar event."
                : "No klydos available for this calendarEvent."}
            </DSTypography>
          </DSBox>
        )}
      </DSBox>

      {selectedKlydo && (
        <DSDialog
          open={klydoModalOpen}
          onClose={() => setKlydoModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={setKlydoModalOpen}
          />
        </DSDialog>
      )}
    </DSDialogContent>
  );
};

export default CalendarEventPreview;
